import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { plata, peso, hoy, conComa } from '../utils/formato';
import { LITROS_POR_TINA, kilosPorTina } from '../utils/tina';

// La cuenta del mes.
//
// No es contabilidad de libros: es lo que hay que poder mirar a fin de mes y
// entender sin un contador al lado. Qué entró, qué salió y qué quedó.
//
// Todo sale de lo que ya se registra en las otras pantallas. No hay nada que
// teclear aparte, y eso es a propósito: una contabilidad que se lleva a mano
// se desincroniza de la realidad en dos semanas y entonces hay dos verdades.

const primeroDelMes = () => {
  const d = new Date();
  d.setDate(1);
  return d.toISOString().split('T')[0];
};

function Contabilidad() {
  const [datos, setDatos] = useState(null);
  const [desde, setDesde] = useState(primeroDelMes());
  const [hasta, setHasta] = useState(hoy());
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');

  const cargar = useCallback(() => {
    setCargando(true);
    setError('');
    axios.get(`/api/contabilidad?desde=${desde}&hasta=${hasta}`)
      .then(r => setDatos(r.data))
      .catch(() => setError('No se pudo cargar la contabilidad'))
      .finally(() => setCargando(false));
  }, [desde, hasta]);

  useEffect(cargar, [cargar]);

  // Los atajos cortos: hoy, la semana, el mes. Son los que usa quien mira el
  // negocio todos los días — «últimos 6 meses» es para revisar, no para
  // trabajar, y era lo único que había.
  const atajoCorto = (que) => {
    const d = new Date();
    if (que === 'hoy') { setDesde(hoy()); setHasta(hoy()); return; }
    if (que === 'semana') {
      // De lunes a hoy. El domingo cuenta como fin de la semana que pasó, que
      // es como se piensa aquí: la semana arranca el lunes.
      const dia = (d.getDay() + 6) % 7;
      d.setDate(d.getDate() - dia);
    } else {
      d.setDate(1);
    }
    setDesde(d.toISOString().split('T')[0]);
    setHasta(hoy());
  };

  const atajo = (meses) => {
    const d = new Date();
    d.setMonth(d.getMonth() - meses);
    d.setDate(1);
    setDesde(d.toISOString().split('T')[0]);
    setHasta(hoy());
  };

  const v = (x) => (datos && datos[x] ? (datos[x].v || 0) : 0);

  // ── Lo que entra y lo que sale ──────────────────────────────────────────
  const ventasContado = v('ventas_contado');
  const ventasCredito = v('ventas_credito');
  const ventaGanado   = v('venta_ganado');
  const entra = ventasContado + ventasCredito + ventaGanado;

  const leche   = v('leche');
  const insumos = v('insumos');
  const nomina  = v('nomina');
  const gastos  = v('gastos');
  const compraGanado = v('compra_ganado');
  const vetGanado    = v('vet_ganado');
  const sale = leche + insumos + nomina + gastos + compraGanado + vetGanado;

  const queda = entra - sale;

  // ── LA CAJA, que NO es lo mismo que la utilidad ─────────────────────────
  //
  // Arriba se cuenta lo VENDIDO y lo RECIBIDO, esté pagado o no: eso dice si
  // el negocio da o no da, y es lo correcto para saberlo.
  //
  // Pero no es la plata que hay. Un mes puede cerrar con ocho millones de
  // utilidad y no haber con qué pagar la nómina, porque cuatro están fiados
  // en la calle y la leche del finquero todavía se debe.
  //
  // Aquí se cuenta lo que de verdad entró y salió del cajón: lo cobrado en
  // vez de lo vendido, y lo pagado a las fincas en vez de la leche recibida.
  // `pagos_fincas.monto` guarda el NETO —lo que se le entregó al finquero—,
  // que es justo lo que salió del cajón.
  const cobrado = v('cobrado');
  const pagadoFincas = v('pagado_fincas');
  const cajaEntra = ventasContado + cobrado + ventaGanado;
  const cajaSale = pagadoFincas + insumos + nomina + gastos + compraGanado + vetGanado;
  const caja = cajaEntra - cajaSale;

  // El costo del kilo producido en el periodo, que es la cifra que manda en
  // este negocio. No incluye el ganado: eso es otra cuenta.
  const kilos = datos?.produccion?.kilos || 0;
  const litros = datos?.produccion?.litros || 0;
  const costoProduccion = leche + insumos + nomina;
  const costoKilo = kilos > 0 ? costoProduccion / kilos : null;
  const rendimiento = kilos > 0 ? litros / kilos : null;

  const Fila = ({ etiqueta, monto, pie, fuerte, color }) => (
    <tr>
      <td className={fuerte ? 'recio' : ''}>
        {etiqueta}
        {pie && <div style={{ fontSize: 11, color: 'var(--tinta-tenue)', fontWeight: 400 }}>{pie}</div>}
      </td>
      <td className={'num' + (fuerte ? ' recio' : '')} style={color ? { color } : undefined}>
        {plata(monto)}
      </td>
    </tr>
  );

  return (
    <>
      <div className="encabezado">
        <h1>Contabilidad</h1>
        <p>Qué entró, qué salió y qué quedó</p>
      </div>

      {error && <div className="aviso malo">{error}</div>}

      <div className="bloque">
        <h2>📅 Periodo</h2>
        <div className="cuerpo">
          <div className="campos">
            <label className="campo">
              <span>Desde</span>
              <input type="date" value={desde} onChange={e => setDesde(e.target.value)} />
            </label>
            <label className="campo">
              <span>Hasta</span>
              <input type="date" value={hasta} onChange={e => setHasta(e.target.value)} />
            </label>
          </div>
          <div className="acciones">
            <button className="boton suave chico" onClick={() => { setDesde(primeroDelMes()); setHasta(hoy()); }}>
              Este mes
            </button>
            <button className="boton suave chico" onClick={() => atajoCorto('hoy')}>Hoy</button>
            <button className="boton suave chico" onClick={() => atajoCorto('semana')}>Esta semana</button>
            <button className="boton suave chico" onClick={() => atajoCorto('mes')}>Este mes</button>
            <button className="boton suave chico" onClick={() => atajo(1)}>Últimos 2 meses</button>
            <button className="boton suave chico" onClick={() => atajo(5)}>Últimos 6 meses</button>
            <button className="boton suave chico" onClick={() => atajo(11)}>Último año</button>
          </div>
        </div>
      </div>

      {cargando ? (
        <p style={{ color: 'var(--tinta-tenue)' }}>Cargando…</p>
      ) : !datos ? null : (
        <>
          {/* LA FRASE, antes de los números.
              
              Gabriel no es contador. Una tabla de doce renglones no le dice si
              le fue bien: eso hay que decírselo con palabras, y el matiz de lo
              fiado tiene que ir en la misma frase o no se lee.
              
              Se arma con las mismas cifras de las tarjetas — no hay una
              segunda cuenta que pueda decir otra cosa. */}
          {entra > 0 && (
            <div className={'aviso ' + (queda >= 0 ? 'bueno' : 'malo')}
                 style={{ fontSize: 16, lineHeight: 1.6 }}>
              Vendió <strong>{plata(entra)}</strong>, le costó{' '}
              <strong>{plata(sale)}</strong>
              {queda >= 0
                ? <> y le quedaron <strong>{plata(queda)}</strong>.</>
                : <> y le faltaron <strong>{plata(Math.abs(queda))}</strong>.</>}
              {ventasCredito > 0 && (
                <> Ojo: <strong>{plata(ventasCredito)}</strong> de esas ventas
                  se fiaron, así que esa plata todavía está en la calle.</>
              )}
              {/* La advertencia que de verdad importa: utilidad buena con caja
                  en rojo es no tener con qué pagar la nómina. */}
              {queda > 0 && caja < 0 && (
                <> <strong>Y en caja quedó en rojo</strong> ({plata(caja)}): el
                  negocio dio, pero la plata todavía no ha entrado.</>
              )}
            </div>
          )}

          <div className="rejilla">
            <div className="dato" style={{ '--color': 'var(--venta)' }}>
              <div className="rotulo">📥 Entró</div>
              <div className="valor cifra">{plata(entra)}</div>
              <div className="pie">ventas y ganado vendido</div>
            </div>
            <div className="dato" style={{ '--color': 'var(--malo)' }}>
              <div className="rotulo">📤 Salió</div>
              <div className="valor cifra">{plata(sale)}</div>
              <div className="pie">leche, insumos, gente y gastos</div>
            </div>
            {/* «Quedó» se leía como «me quedó esta plata en el bolsillo», y
                no es eso: es la utilidad, que cuenta lo fiado como vendido.
                Se nombra por lo que es, y la caja va al lado en vez de
                escondida tres bloques más abajo. */}
            <div className="dato" style={{ '--color': queda >= 0 ? 'var(--bueno)' : 'var(--malo)' }}>
              <div className="rotulo">{queda >= 0 ? '✅ Utilidad' : '⚠️ Pérdida'}</div>
              <div className="valor cifra">{plata(Math.abs(queda))}</div>
              <div className="pie">
                {/* Sin el signo: la tarjeta ya dice «Pérdida». Y con coma,
                    que en Colombia el punto separa los miles. */}
                {entra > 0
                  ? `${conComa(Math.round(Math.abs(queda) / entra * 1000) / 10)}% de lo que vendió`
                  : '—'}
              </div>
            </div>
            <div className="dato" style={{ '--color': caja >= 0 ? 'var(--leche)' : 'var(--malo)' }}>
              <div className="rotulo">💵 En caja</div>
              <div className="valor cifra"
                   style={caja < 0 ? { color: 'var(--malo)' } : undefined}>
                {plata(caja)}
              </div>
              <div className="pie">lo cobrado menos lo pagado de verdad</div>
            </div>
            {costoKilo && (
              <div className="dato" style={{ '--color': 'var(--queso)' }}>
                <div className="rotulo">🧀 Costo del kilo</div>
                <div className="valor cifra">{plata(costoKilo)}</div>
                <div className="pie">
                  {Math.round(litros / LITROS_POR_TINA)} tinas → {peso(kilos, 'kg')}
                  {kilosPorTina(rendimiento) && <> · {conComa(kilosPorTina(rendimiento))} kg por tina</>}
                </div>
                <div className="pie" style={{ opacity: .7 }}>
                  {conComa(Math.round(rendimiento * 100) / 100)} L/kg
                </div>
              </div>
            )}
          </div>

          <div className="bloque">
            <h2>📥 Lo que entró</h2>
            <div className="tabla-caja">
              <table className="datos">
                <tbody>
                  <Fila etiqueta="Ventas de contado" monto={ventasContado}
                        pie="mostrador y mayoristas que pagaron" />
                  <Fila etiqueta="Ventas fiadas" monto={ventasCredito}
                        pie="facturado, aunque no todo esté cobrado" />
                  <Fila etiqueta="Ganado vendido" monto={ventaGanado} />
                  <Fila etiqueta="Total" monto={entra} fuerte color="var(--venta)" />
                </tbody>
              </table>
            </div>
            {ventasCredito > 0 && (
              <div className="cuerpo" style={{ paddingTop: 0 }}>
                <div className="aviso ojo" style={{ marginBottom: 0 }}>
                  <strong>Ojo con lo fiado.</strong> Los {plata(ventasCredito)} de ventas a
                  crédito cuentan como que entraron, pero la plata todavía puede estar en
                  la calle. En efectivo, en el periodo, solo se cobraron{' '}
                  <strong>{plata(v('cobrado'))}</strong> de abonos.
                </div>
              </div>
            )}
          </div>

          <div className="bloque">
            <h2>📤 Lo que salió</h2>
            <div className="tabla-caja">
              <table className="datos">
                <tbody>
                  <Fila etiqueta="Leche comprada" monto={leche}
                        pie={datos.leche?.litros
                          ? `${peso(datos.leche.litros, 'L')} a las fincas`
                          : null} />
                  <Fila etiqueta="Insumos" monto={insumos} pie="cuajo, sal, empaque" />
                  <Fila etiqueta="Nómina" monto={nomina} pie="lo pagado a la gente, neto" />
                  <Fila etiqueta="Gastos" monto={gastos} pie="energía, transporte, lo demás" />
                  <Fila etiqueta="Compra de ganado" monto={compraGanado} />
                  <Fila etiqueta="Veterinario y cuidados" monto={vetGanado} />
                  <Fila etiqueta="Total" monto={sale} fuerte color="var(--malo)" />
                </tbody>
              </table>
            </div>
            {compraGanado > 0 && (
              <div className="cuerpo" style={{ paddingTop: 0 }}>
                <div className="aviso" style={{ marginBottom: 0 }}>
                  El ganado se cuenta el día que se compra y el día que se vende, no
                  mes a mes. Un mes con una compra grande se ve terrible sin serlo:
                  esa plata no se gastó, se cambió por animales que siguen ahí.
                </div>
              </div>
            )}
          </div>

          {kilos > 0 && (
            <div className="bloque">
              <h2>🧀 De dónde sale el costo del kilo</h2>
              <div className="tabla-caja">
                <table className="datos">
                  <tbody>
                    <Fila etiqueta="Leche" monto={leche} />
                    <Fila etiqueta="Insumos" monto={insumos} />
                    <Fila etiqueta="Mano de obra" monto={nomina} />
                    <Fila etiqueta="Costo de producir" monto={costoProduccion} fuerte />
                    <tr>
                      <td className="recio">Kilos producidos</td>
                      <td className="num recio">{peso(kilos, 'kg')}</td>
                    </tr>
                    <tr>
                      <td className="recio">Costo por kilo</td>
                      <td className="num recio" style={{ fontSize: 17, color: 'var(--queso)' }}>
                        {plata(costoKilo)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="cuerpo" style={{ paddingTop: 12 }}>
                <p style={{ margin: 0, fontSize: 13, color: 'var(--tinta-tenue)' }}>
                  Esta es la cifra que decide el precio de venta. Si un kilo cuesta{' '}
                  {plata(costoKilo)} producirlo, todo lo que se venda por debajo de eso
                  pierde plata aunque la caja se vea llena.
                </p>
              </div>
            </div>
          )}

          <div className="bloque">
            <h2>⏳ Lo que queda pendiente</h2>
            <div className="tabla-caja">
              <table className="datos">
                <tbody>
                  <tr>
                    <td className="recio">Le deben a usted</td>
                    <td className="num recio" style={{ color: 'var(--venta)' }}>
                      {plata(v('por_cobrar'))}
                      <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--tinta-tenue)' }}>
                        clientes a crédito
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="recio">Usted debe</td>
                    <td className="num recio" style={{ color: 'var(--malo)' }}>
                      {plata(v('por_pagar'))}
                      <div style={{ fontSize: 11, fontWeight: 400, color: 'var(--tinta-tenue)' }}>
                        leche recibida sin pagar
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="recio">Diferencia</td>
                    <td className="num recio"
                        style={{ color: v('por_cobrar') - v('por_pagar') >= 0
                                        ? 'var(--bueno)' : 'var(--malo)' }}>
                      {plata(v('por_cobrar') - v('por_pagar'))}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="cuerpo" style={{ paddingTop: 12 }}>
              <p style={{ margin: 0, fontSize: 13, color: 'var(--tinta-tenue)' }}>
                Estas dos cifras son de siempre, no del periodo: es todo lo que
                está pendiente hoy. Si lo que debe es mucho más de lo que le
                deben, la caja va a apretar aunque el mes se vea bueno.
              </p>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default Contabilidad;
